import {
    datatableEditResourcesActions,
    datatableIconAction,
    datatableIconActionsRow,
    datatableViewEditResourcesActions,
} from './icon-actions';
import {assignHtmlDatatableColumns, syncDatatableColumnFiltering} from '../cbar-datatable';
import {prepareDatatableScrollChrome} from '../common/datatable-scroll-hint';
import {documentReady} from '../document-ready';
import {
    actionColumn,
    datatableNeedsColumnInit,
    htmlColumn,
    setupDatatable,
    setupDatatables,
    sortableDateColumn,
    sortableUnsafeHtmlDateColumn,
    textColumn,
    unsafeActionColumn,
    unsafeHtmlColumn,
    type ColumnBuilder,
    type DatatableColumn,
    type DatatableElement,
} from '../common/datatable-page';
import type {CbarDatatableContext} from '../cbar-datatable';

type HtmlCellFn = NonNullable<typeof window.CBAR>['datatableHtmlCell'];
type UnsafeHtmlFn = NonNullable<typeof window.CBAR>['unsafeHTML'];
type CellInfo = {getValue: () => unknown; row: {original: Record<string, unknown>}};

function unsafeHTMLFn(): UnsafeHtmlFn {
    return window.CBAR!.unsafeHTML!;
}

function nullableUnsafeCell(
    html: CbarDatatableContext['html'],
    unsafeHTML: UnsafeHtmlFn,
    info: CellInfo,
): unknown {
    const val = info.getValue();
    return val == null || val === '' ? '' : html`${unsafeHTML!(val as string)}`;
}

function stripHtmlTags(value: string): string {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function unsafeHtmlColumnWithTextFilter(
    ch: CbarDatatableContext['columnHelper'],
    html: CbarDatatableContext['html'],
    unsafeHTML: UnsafeHtmlFn,
    key: string,
    header: string,
    options: DatatableColumn = {},
): DatatableColumn {
    return ch.accessor(key, {
        header: () => header,
        cell: (info: CellInfo) => {
            const value = info.getValue();
            return value == null || value === '' ? '' : html`${unsafeHTML!(value as string)}`;
        },
        enableSorting: false,
        filterFn: (row, columnId, filterValue) => {
            if (filterValue == null || filterValue === '') {
                return true;
            }

            const cellValue = row.getValue(columnId);
            const text = stripHtmlTags(String(cellValue ?? '')).toLowerCase();
            return text.includes(String(filterValue).toLowerCase());
        },
        ...options,
    });
}

function buildNullableUnsafeColumns(
    ch: CbarDatatableContext['columnHelper'],
    html: CbarDatatableContext['html'],
    unsafeHTML: UnsafeHtmlFn,
    specs: Array<[string, string, DatatableColumn?]>,
): DatatableColumn[] {
    return specs.map(([key, header, options = {}]) =>
        ch.accessor(key, {
            header: () => header,
            cell: (info: CellInfo) => nullableUnsafeCell(html, unsafeHTML, info),
            enableSorting: false,
            ...options,
        }),
    );
}

function buildAdministratorsColumns(
    {columnHelper: ch, html}: CbarDatatableContext & {htmlCell: HtmlCellFn},
    table: DatatableElement,
): DatatableColumn[] {
    const unsafeHTML = unsafeHTMLFn();
    const superAdmin = table.dataset.superAdmin === '1';
    const columns: DatatableColumn[] = [
        unsafeHtmlColumn(ch, html, unsafeHTML!, '0', 'Name'),
        unsafeActionColumn(ch, html, unsafeHTML!, '1'),
    ];

    if (superAdmin) {
        columns.push(
            unsafeHtmlColumn(ch, html, unsafeHTML!, '2', 'Auth Source'),
            unsafeActionColumn(ch, html, unsafeHTML!, '3'),
        );
    } else {
        columns.push(unsafeActionColumn(ch, html, unsafeHTML!, '2'));
    }

    return columns;
}

function buildListTagsColumns(
    ctx: CbarDatatableContext & {htmlCell: HtmlCellFn},
    table: DatatableElement,
): DatatableColumn[] {
    const {columnHelper: ch, html} = ctx;
    const unsafeHTML = unsafeHTMLFn();
    const dataEl = table.id ? document.getElementById(`${table.id}-data`) : null;

    if (dataEl?.textContent) {
        try {
            table.data = JSON.parse(dataEl.textContent);
        } catch {
            table.data = [];
        }
    }

    const displayTagSets = table.dataset.displayTagSets === '1';
    const hasActions = table.dataset.hasActions === '1';

    const columns: DatatableColumn[] = [
        unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML!, '0', 'Title'),
        unsafeHtmlColumn(ch, html, unsafeHTML!, '1', 'Tag Type', {enableColumnFilter: false}),
        ch.accessor('2', {
            header: () => 'Description',
            cell: (info) => info.getValue() ?? '',
            enableSorting: false,
        }),
        unsafeHtmlColumn(ch, html, unsafeHTML!, '3', 'Tag Code', {enableColumnFilter: false}),
    ];

    if (displayTagSets) {
        columns.push(unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML!, '4', 'Tag Sets'));
    }

    if (hasActions) {
        const actionKey = displayTagSets ? '5' : '4';
        columns.push({
            ...unsafeActionColumn(ch, html, unsafeHTML!, actionKey),
            size: 65,
        });
    }

    return columns;
}

function buildInvoiceListPartialColumns(
    ctx: CbarDatatableContext & {htmlCell: HtmlCellFn},
    table: DatatableElement,
): DatatableColumn[] {
    const {columnHelper: ch, html} = ctx;
    const unsafeHTML = unsafeHTMLFn();
    const payorLabel = table.dataset.payorLabel || 'Payor';
    const columns: DatatableColumn[] = [];

    if (table.dataset.displayCheckbox === '1') {
        columns.push({
            ...unsafeHtmlColumn(ch, html, unsafeHTML!, 'checkbox_html', ''),
            size: 30,
        });
    }

    columns.push(
        unsafeHtmlColumn(ch, html, unsafeHTML!, 'payor_html', payorLabel),
        unsafeHtmlColumn(ch, html, unsafeHTML!, 'invoice_html', 'Invoice No.'),
        textColumn(ch, 'date', 'Date'),
        ch.accessor('status', {
            header: () => 'Status',
            cell: (info: CellInfo) => {
                const value = info.getValue();
                const row = info.row.original;
                if (row.is_danger) {
                    return html`<span class="text-danger">${value as string}</span>`;
                }
                return value;
            },
            enableSorting: false,
        }),
        unsafeHtmlColumn(ch, html, unsafeHTML!, 'amount_html', 'Amount', {align: 'right'}),
    );

    if (table.dataset.displayUnpaid === '1') {
        columns.push(unsafeHtmlColumn(ch, html, unsafeHTML!, 'unpaid_html', 'Unpaid', {align: 'right'}));
    }
    if (table.dataset.displayAllocated === '1') {
        columns.push(unsafeHtmlColumn(ch, html, unsafeHTML!, 'allocated_html', 'Allocated Amount', {align: 'right'}));
    }
    if (table.dataset.displayCampaign === '1') {
        columns.push(unsafeHtmlColumn(ch, html, unsafeHTML!, 'campaign_html', 'Campaign'));
    }
    if (table.dataset.displayActions === '1') {
        columns.push({
            ...unsafeActionColumn(ch, html, unsafeHTML!, 'actions_html'),
            size: 90,
        });
    }
    if (table.dataset.displayAllocate === '1') {
        columns.push(unsafeHtmlColumn(ch, html, unsafeHTML!, 'allocate_html', 'Allocate Amount'));
    }

    return columns;
}

function buildListPaymentsPartialColumns(
    ctx: CbarDatatableContext & {htmlCell: HtmlCellFn},
    table: DatatableElement,
): DatatableColumn[] {
    const {columnHelper: ch, html, htmlCell} = ctx;
    const unsafeHTML = unsafeHTMLFn();
    const columns: DatatableColumn[] = [];

    if (table.dataset.displayCheckbox === '1') {
        columns.push({
            ...unsafeHtmlColumn(ch, html, unsafeHTML!, 'checkbox_html', ''),
            size: 30,
        });
    }

    columns.push(
        textColumn(ch, 'date', 'Receive Date'),
        htmlColumn(ch, htmlCell!, 'payment_type', 'Payment Type'),
    );

    if (table.dataset.displayPayor === '1') {
        columns.push(unsafeHtmlColumn(ch, html, unsafeHTML!, 'payor_html', 'Payor'));
    }
    if (table.dataset.displayCampaign === '1') {
        columns.push(unsafeHtmlColumn(ch, html, unsafeHTML!, 'campaign_html', 'Campaign'));
    }

    columns.push(
        unsafeHtmlColumn(ch, html, unsafeHTML!, 'amount', 'Amount', {align: 'right'}),
        textColumn(ch, 'status', 'Status'),
        textColumn(ch, 'receipt', 'Receipt Number'),
        {
            ...unsafeActionColumn(ch, html, unsafeHTML!, 'actions_html'),
            size: 40,
        },
    );

    return columns;
}

function buildFinancialAccountsColumns(
    ctx: CbarDatatableContext & {htmlCell: HtmlCellFn},
    table: DatatableElement,
): DatatableColumn[] {
    const {columnHelper: ch, html} = ctx;
    const unsafeHTML = unsafeHTMLFn();
    const displayAccountSets = table.dataset.displayAccountSets === '1';
    const hasActions = table.dataset.hasActions === '1';

    const columns: DatatableColumn[] = [
        unsafeHtmlColumn(ch, html, unsafeHTML!, '0', 'Title'),
        unsafeHtmlColumn(ch, html, unsafeHTML!, '1', 'Account Code'),
        unsafeHtmlColumn(ch, html, unsafeHTML!, '2', 'Header Account'),
        unsafeHtmlColumn(ch, html, unsafeHTML!, '3', 'Header Account Code'),
        unsafeHtmlColumn(ch, html, unsafeHTML!, '4', 'Account Type'),
        unsafeHtmlColumn(ch, html, unsafeHTML!, '5', 'Status'),
        unsafeHtmlColumn(ch, html, unsafeHTML!, '6', 'Reserved?'),
    ];

    if (displayAccountSets) {
        columns.push(unsafeHtmlColumn(ch, html, unsafeHTML!, '7', 'Account Set'));
    }

    if (hasActions) {
        const actionKey = displayAccountSets ? '8' : '7';
        columns.push({
            ...unsafeActionColumn(ch, html, unsafeHTML!, actionKey),
            size: 65,
        });
    }

    return columns;
}

function buildSalesItemsColumns(
    ctx: CbarDatatableContext & {htmlCell: HtmlCellFn},
    table: DatatableElement,
): DatatableColumn[] {
    const {columnHelper: ch, html, htmlCell} = ctx;
    const unsafeHTML = unsafeHTMLFn();
    const hasActions = table.dataset.hasActions === '1';

    const columns: DatatableColumn[] = [
        unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML!, '0', 'Title'),
        ch.accessor('1', {
            header: () => 'Description',
            cell: (info) => info.getValue() ?? '',
            enableSorting: false,
        }),
        htmlColumn(ch, htmlCell!, '2', 'Amount', {enableColumnFilter: false}),
        htmlColumn(ch, htmlCell!, '3', 'Tax', {enableColumnFilter: false}),
        htmlColumn(ch, htmlCell!, '4', 'Total Amount', {enableColumnFilter: false}),
        htmlColumn(ch, htmlCell!, '5', 'Catalogues', {enableColumnFilter: false}),
        htmlColumn(ch, htmlCell!, '6', 'Attributes', {enableColumnFilter: false}),
    ];

    if (hasActions) {
        columns.push({
            ...actionColumn(ch, htmlCell!, '7'),
            size: 65,
        });
    }

    return columns;
}

function buildSalesBundlesColumns(
    ctx: CbarDatatableContext & {htmlCell: HtmlCellFn},
): DatatableColumn[] {
    const {columnHelper: ch, html, htmlCell} = ctx;
    const unsafeHTML = unsafeHTMLFn();

    return [
        unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML!, '0', 'Title'),
        ch.accessor('1', {
            header: () => 'Description',
            cell: (info) => info.getValue() ?? '',
            enableSorting: false,
        }),
        ch.accessor('2', {
            header: () => 'Type',
            cell: (info) => info.getValue() ?? '',
            enableSorting: false,
            enableColumnFilter: false,
        }),
        htmlColumn(ch, htmlCell!, '3', 'Amount', {enableColumnFilter: false}),
        ch.accessor('4', {
            header: () => 'Items',
            cell: (info) => info.getValue() ?? '',
            enableSorting: false,
            enableColumnFilter: false,
        }),
        htmlColumn(ch, htmlCell!, '5', 'Catalogues', {enableColumnFilter: false}),
        ch.accessor('6', {
            header: () => 'Added Date',
            cell: (info) => info.getValue() ?? '',
            enableSorting: false,
            enableColumnFilter: false,
        }),
        {...actionColumn(ch, htmlCell!, '7'), size: 65},
    ];
}

function buildCampaignDetailsColumns(
    ctx: CbarDatatableContext & {htmlCell: HtmlCellFn},
): DatatableColumn[] {
    const {columnHelper: ch, htmlCell} = ctx;

    return [
        {
            ...htmlColumn(ch, htmlCell!, 'campaign_name', 'Campaign Name'),
            size: 200,
        },
        htmlColumn(ch, htmlCell!, 'campaign_code', 'Campaign Code'),
        {
            ...htmlColumn(ch, htmlCell!, 'amount', 'Total Raised'),
            enableColumnFilter: false,
        },
        {
            ...actionColumn(ch, htmlCell!, 'actions'),
            header: 'Actions',
            size: 128,
        },
    ];
}

const PROFILE_BUILDERS: Record<string, ColumnBuilder> = {
    'billing-payments': ({columnHelper: ch, html}) => [
        textColumn(ch, 'date', 'Date'),
        textColumn(ch, 'cost', 'Cost'),
        ch.accessor('receipt', {
            header: () => 'Receipt',
            cell: (info) => {
                const url = info.getValue() as string | null;
                if (!url) {
                    return '';
                }

                const separator = url.includes('?') ? '&' : '?';
                return datatableIconAction(html, {
                    href: `${url}${separator}download=1`,
                    label: 'Download receipt',
                    icon: 'download',
                    variant: 'tonal',
                });
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ],
    'billing-payors': ({columnHelper: ch, html}) => [
        ch.accessor('name', {
            header: () => 'Name',
            cell: (info) => {
                const val = info.getValue() as {url?: string; value?: string} | null;
                if (val?.url) {
                    return html`<a href="${val.url}">${val.value ?? ''}</a>`;
                }
                return val?.value ?? '';
            },
        }),
        textColumn(ch, 'organization', 'Organisation'),
        textColumn(ch, 'payments', 'Payments'),
    ],
    administrators: buildAdministratorsColumns,
    'list-organisations': ({columnHelper: ch, html}) => [
        ch.accessor('id', {
            header: () => 'ID',
            cell: (info) => info.getValue(),
        }),
        ch.accessor('organisation_name', {
            header: () => 'Organisation Name',
            cell: (info) =>
                html`<a href="${info.row.original.organisation_url}">${info.getValue()}</a>`,
        }),
        ch.accessor('actions', {
            header: () => 'Actions',
            cell: (info) => {
                const val = info.getValue() as {update?: string; resources?: string} | null;
                if (val?.update && val.resources) {
                    return datatableEditResourcesActions(html, {
                        update: val.update,
                        resources: val.resources,
                    }, {edit: 'Edit Organisation', resources: 'Update Resources'});
                }
                return '';
            },
        }),
        ch.accessor('total_amount', {
            header: () => 'Total Amount',
            cell: (info) => {
                const vals = (info.getValue() as Array<{amount: string; currency?: string}>) || [];
                return html`<div style="text-align: right;">
                    ${vals.map((v) =>
                        v.currency
                            ? html`<div class='pull-right'>${v.amount} <span class="label label-currency">${v.currency}</span></div>`
                            : html`<div class='pull-right'>${v.amount}</div>`,
                    )}
                </div>`;
            },
        }),
    ],
    catalogues: ({columnHelper: ch, htmlCell}) => [
        htmlColumn(ch, htmlCell!, '0', 'Title', {enableSorting: false}),
        htmlColumn(ch, htmlCell!, '1', 'Status', {enableSorting: false}),
        htmlColumn(ch, htmlCell!, '2', 'Type', {enableSorting: false}),
        htmlColumn(ch, htmlCell!, '3', 'Modified Date', {enableSorting: false}),
        htmlColumn(ch, htmlCell!, '4', 'Available in', {enableSorting: false}),
        actionColumn(ch, htmlCell!, '5'),
    ],
    'list-tags': buildListTagsColumns,
    'invoice-list-partial': buildInvoiceListPartialColumns,
    'list-payments-partial': buildListPaymentsPartialColumns,
    'intra-entity-matches': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return [
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'entity', 'Entity'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'amount', 'Amount', {align: 'right'}),
            textColumn(ch, 'status', 'Status'),
            textColumn(ch, 'date', 'Date'),
            textColumn(ch, 'relationship', 'Relationship'),
            textColumn(ch, 'description', 'Description'),
        ];
    },
    'payment-entity-matches': ({columnHelper: ch, html}, table) => {
        const unsafeHTML = unsafeHTMLFn();
        const columns: DatatableColumn[] = [];

        if (table.dataset.showPayment === '1') {
            columns.push(
                unsafeHtmlColumn(ch, html, unsafeHTML!, 'payment_html', 'Payment'),
                unsafeHtmlColumn(ch, html, unsafeHTML!, 'payment_type', 'Type'),
                unsafeHtmlColumn(ch, html, unsafeHTML!, 'payment_amount', 'Amount'),
                textColumn(ch, 'receipt', 'Receipt'),
            );
        }

        columns.push(
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'description', 'Description'),
            textColumn(ch, 'payment_date', 'Payment Date'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'allocated_amount', 'Allocated Amount', {align: 'right'}),
        );

        return columns;
    },
    'financial-accounts': buildFinancialAccountsColumns,
    'sales-items': buildSalesItemsColumns,
    'sales-bundles': buildSalesBundlesColumns,
    'included-in-catalogue': ({columnHelper: ch, htmlCell}) => [
        htmlColumn(ch, htmlCell!, 'catalogue', 'Catalogue', {enableSorting: false}),
        htmlColumn(ch, htmlCell!, 'available_in', 'Available in', {enableSorting: false}),
        textColumn(ch, 'modified_date', 'Modified Date', {enableSorting: false}),
        actionColumn(ch, htmlCell!, 'actions'),
    ],
    'campaign-details': (ctx) => buildCampaignDetailsColumns(ctx),
};

const TABLE_BUILDERS: Record<string, ColumnBuilder> = {
    'org-payments-list': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return [
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'id', 'ID'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'entity_type', 'PaymentEntity'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'entity_id', 'PaymentEntityID'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'date', 'Date'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'status', 'Status'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'amount', 'Amount'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'payor', 'Payor'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'campaign', 'Campaign'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'organization', 'Organization'),
        ];
    },
    'report-transaction-table': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return [
            textColumn(ch, 'date', 'Date/Time'),
            textColumn(ch, 'campaign_title', 'Campaign'),
            textColumn(ch, 'payor', 'Donor'),
            unsafeHtmlColumn(ch, html, unsafeHTML!, 'amount_html', 'Amount', {align: 'right'}),
            textColumn(ch, 'recurring', 'Recurring?'),
            textColumn(ch, 'source', 'Source'),
        ];
    },
    'bank-file-table': ({columnHelper: ch, htmlCell}) => [
        textColumn(ch, '0', 'ID'),
        htmlColumn(ch, htmlCell!, '1', 'File'),
        textColumn(ch, '2', 'Date'),
        textColumn(ch, '3', 'Organization'),
        textColumn(ch, '4', 'Type'),
        htmlColumn(ch, htmlCell!, '5', 'Size'),
        textColumn(ch, '6', 'Target Table'),
    ],
    'tagsets-table': ({columnHelper: ch, html}) => [
        ch.accessor('title', {
            header: () => 'Title',
            cell: (info) => {
                const val = info.getValue() as {url?: string; value?: string} | null;
                if (val?.url) {
                    return html`<a href="${val.url}">${val.value ?? ''}</a>`;
                }
                return val?.value ?? '';
            },
        }),
        textColumn(ch, 'description', 'Description'),
        textColumn(ch, 'modified_date', 'Modified Date'),
        ch.accessor('action', {
            header: () => '',
            cell: (info) => {
                const val = info.getValue() as {edit_url?: string; edit_title?: string} | null;
                if (val?.edit_url) {
                    return datatableIconAction(html, {
                        href: val.edit_url,
                        label: val.edit_title ?? 'Edit',
                        icon: 'edit',
                        className: 'icon_link datatable-action-link',
                    });
                }
                return '';
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ],
    sales_bundles: ({columnHelper: ch, htmlCell}) => [
        htmlColumn(ch, htmlCell!, 'bundle', 'Bundle', {enableSorting: false}),
        textColumn(ch, 'modified_date', 'Modified Date', {enableSorting: false}),
        actionColumn(ch, htmlCell!, 'actions'),
    ],
    'direct-entry-batches': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return buildNullableUnsafeColumns(ch, html, unsafeHTML, [
            ['0', 'Batch'],
            ['1', 'Type'],
            ['2', 'Date'],
            ['3', 'Total Debit'],
            ['4', 'Total Credit'],
            ['5', '', {enableColumnFilter: false}],
        ]);
    },
    'new-direct-entry-payments': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return buildNullableUnsafeColumns(ch, html, unsafeHTML, [
            ['0', '', {enableColumnFilter: false}],
            ['1', 'Type'],
            ['2', 'Status'],
            ['3', 'Added Date'],
            ['4', 'Actioned Date'],
            ['5', 'Account Name'],
            ['6', 'Customer Reference'],
            ['7', 'Amount'],
            ['8', 'Campaign'],
            ['9', 'Invoice'],
            ['10', '', {enableColumnFilter: false}],
        ]);
    },
    'direct-entry-batch-payments': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return buildNullableUnsafeColumns(ch, html, unsafeHTML, [
            ['0', 'ID'],
            ['1', 'Type'],
            ['2', 'Status'],
            ['3', 'Sync'],
            ['4', 'Actioned Date'],
            ['5', 'Account Name'],
            ['6', 'Customer Reference'],
            ['7', 'Amount', {align: 'right'}],
            ['8', 'Campaign'],
            ['9', 'Invoice'],
            ['10', '', {enableColumnFilter: false}],
        ]);
    },
    'campaign-details-table': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return buildNullableUnsafeColumns(ch, html, unsafeHTML, [
            ['0', 'Payor'],
            ['1', 'Type'],
            ['2', 'Received Date'],
            ['3', 'Amount'],
            ['4', 'Receipt Sent'],
            ['5', '', {enableColumnFilter: false}],
        ]);
    },
    'auth-source-list': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        const htmlCell = (info: CellInfo) => {
            const val = info.getValue();
            return val ? html`${unsafeHTML!(val as string)}` : '';
        };

        return [
            ch.accessor('auth_source', {header: () => 'Auth Source', cell: htmlCell, enableSorting: false}),
            ch.accessor('type', {header: () => 'Type', enableSorting: false}),
            ch.accessor('identifier', {header: () => 'Identifier', enableSorting: false}),
            ch.accessor('organization', {header: () => 'Organization', cell: htmlCell, enableSorting: false}),
            ch.accessor('payor', {header: () => 'Payor', cell: htmlCell, enableSorting: false}),
            ch.accessor('payor_email', {header: () => 'Payor Email', enableSorting: false}),
            ch.accessor('administrator', {header: () => 'Administor', enableSorting: false}),
        ];
    },
    'recurring-payment-task': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return buildNullableUnsafeColumns(ch, html, unsafeHTML, [
            ['0', 'Created'],
            ['1', 'Title'],
            ['2', 'Schedule'],
            ['3', 'Payment'],
            ['4', '', {enableColumnFilter: false}],
            ['5', 'Unpaid Amount', {meta: {align: 'right'}}],
            ['6', 'Limit Attempt', {meta: {align: 'right'}}],
            ['7', 'Attempt(s)', {meta: {align: 'right'}}],
            ['8', '', {enableColumnFilter: false}],
        ]);
    },
    'payment-details-table': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return [
            unsafeHtmlColumn(ch, html, unsafeHTML, 'payor', 'Payor'),
            unsafeHtmlColumn(ch, html, unsafeHTML, 'payment_instrument', 'Type', {size: 200}),
            unsafeHtmlColumn(ch, html, unsafeHTML, 'date', 'Received Date'),
            unsafeHtmlColumn(ch, html, unsafeHTML, 'amount', 'Amount', {size: 110}),
            unsafeHtmlColumn(ch, html, unsafeHTML, 'transaction_id', 'Transaction ID'),
            {...unsafeActionColumn(ch, html, unsafeHTML, 'action'), size: 80},
        ];
    },
    'transfers-list': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return buildNullableUnsafeColumns(ch, html, unsafeHTML, [
            ['0', '#'],
            ['1', 'Withdrawal Date'],
            ['2', 'From Bank Account'],
            ['3', 'Transferred Amount'],
            ['4', 'To Bank Account'],
            ['5', 'Received Amount'],
            ['6', 'Receive Date'],
            ['7', 'Status'],
            ['8', '', {size: 80, enableColumnFilter: false}],
        ]);
    },
    'targets-list': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return buildNullableUnsafeColumns(ch, html, unsafeHTML, [
            ['0', 'Title'],
            ['1', 'Goal for'],
            ['2', 'Goal value', {align: 'right'}],
            ['3', 'Current value', {align: 'right'}],
            ['4', 'Percent', {align: 'right'}],
            ['5', 'Start date'],
            ['6', 'End date'],
            ['7', 'Status'],
            ['8', '', {enableColumnFilter: false}],
        ]);
    },
    sales_bundle_items: ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        const htmlCell = (info: CellInfo) => nullableUnsafeCell(html, unsafeHTML, info);

        return [
            ch.accessor('0', {header: () => 'Item Title', cell: htmlCell}),
            ch.accessor('1', {header: () => 'Quantity', cell: htmlCell}),
            ch.accessor('2', {header: () => 'Item Original Amount', cell: htmlCell}),
            ch.accessor('3', {header: () => 'Sales Amount', cell: htmlCell}),
            sortableUnsafeHtmlDateColumn(ch, html, unsafeHTML, 'modified_date_sort', 'Modified Date', '4'),
            ch.accessor('5', {header: () => 'Attributes', cell: htmlCell}),
            ch.accessor('6', {header: () => '', cell: htmlCell, enableColumnFilter: false}),
        ];
    },
    'invoice-list': ({columnHelper: ch, html, htmlCell}, table) => {
        table.availableSizes = ['10', '25', '50'];
        table.defaultPageSize = 25;

        return [
            sortableDateColumn(ch, 'date_sort', 'Date', '0', {
                enableSorting: true,
                sortDescFirst: true,
            }),
            htmlColumn(ch, htmlCell!, '1', 'Invoice No'),
            htmlColumn(ch, htmlCell!, '2', 'Other Party'),
            htmlColumn(ch, htmlCell!, '3', 'Type'),
            ch.accessor('4', {
                header: () => 'Status',
                cell: (info) => {
                    const value = info.getValue();
                    if (value === 'Cancelled') {
                        return html`<span class="text-danger">${value as string}</span>`;
                    }
                    return value;
                },
                enableSorting: false,
            }),
            htmlColumn(ch, htmlCell!, '5', 'Amount'),
            htmlColumn(ch, htmlCell!, '6', 'Invoice Sent'),
            htmlColumn(ch, htmlCell!, '7', 'Action'),
        ];
    },
    'show-resources-table': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        const htmlCell = (info: CellInfo) => nullableUnsafeCell(html, unsafeHTML, info);

        return [
            unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML, '0', 'Resource', {enableSorting: true}),
            ch.accessor('1', {header: () => 'Action', cell: htmlCell, width: '100px', enableColumnFilter: false}),
        ];
    },
    'shared-emails-table': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return buildNullableUnsafeColumns(ch, html, unsafeHTML, [
            ['0', 'Contact', {size: 180}],
            ['1', 'Email', {size: 80}],
            ['2', 'Address'],
            ['3', 'Phone'],
            ['4', 'Ext ID', {size: 50}],
            ['5', 'Org Ref', {size: 50}],
            ['6', 'Action', {size: 200}],
        ]);
    },
    'bank-accounts-table': ({columnHelper: ch, html}) => [
        ch.accessor('account_name', {
            header: () => 'Account Name',
            cell: (info) => {
                const row = info.row.original as {payor_id: string; transactions_url: string};
                return html`<a class="popover-profile-widget" payor_id="${row.payor_id}" href="${row.transactions_url}">${info.getValue()}</a>`;
            },
        }),
        textColumn(ch, 'financial_institution', 'Financial Institution'),
        ch.accessor('bsb', {header: () => 'BSB', align: 'right'}),
        ch.accessor('account_number', {header: () => 'Account Number', align: 'right'}),
        sortableDateColumn(ch, 'last_transaction_sort', 'Last Transaction', 'last_transaction', {
            align: 'right',
        }),
        ch.accessor('balance', {header: () => 'Balance', align: 'right'}),
        ch.accessor('currency', {header: () => 'Currency', align: 'right'}),
        ch.accessor('edit_url', {
            header: () => '',
            cell: (info) => {
                const row = info.row.original as {edit_title: string};
                return datatableIconAction(html, {
                    href: info.getValue() as string,
                    label: row.edit_title,
                    icon: 'edit',
                    className: 'icon_link datatable-action-link',
                });
            },
            align: 'right',
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ],
    'recurring-payments-list': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        window.setTimeout(() => {
            if (typeof window.reloadPayorWidget === 'function') {
                window.reloadPayorWidget();
            }
        }, 100);

        return [
            ch.accessor('0', {
                header: () => 'Payor',
                cell: (info: CellInfo) => html`${unsafeHTML!(info.getValue() as string)}`,
            }),
            ch.accessor('1', {
                header: () => 'Payment Instrument',
                cell: (info: CellInfo) => html`${unsafeHTML!(info.getValue() as string)}`,
            }),
            ch.accessor('2', {
                header: () => 'Frequency',
                cell: (info: CellInfo) => html`${unsafeHTML!(info.getValue() as string)}`,
            }),
            sortableUnsafeHtmlDateColumn(ch, html, unsafeHTML, 'start_date_sort', 'Start Date', '3'),
            sortableUnsafeHtmlDateColumn(ch, html, unsafeHTML, 'coming_payment_sort', 'Coming Payment', '4'),
            sortableUnsafeHtmlDateColumn(ch, html, unsafeHTML, 'next_payment_sort', 'New Payment', '5'),
            ch.accessor('6', {
                header: () => 'Amount',
                cell: (info: CellInfo) => html`${unsafeHTML!(info.getValue() as string)}`,
            }),
            ch.accessor('7', {
                header: () => 'Status',
                cell: (info: CellInfo) => {
                    const sData = info.getValue() as string;
                    if (sData === 'Cancelled') {
                        return html`<span class="text-danger">${unsafeHTML!(sData)}</span>`;
                    }
                    return html`${unsafeHTML!(sData)}`;
                },
            }),
            ch.accessor('8', {
                header: () => '',
                cell: (info: CellInfo) => html`${unsafeHTML!(info.getValue() as string)}`,
                enableSorting: false,
                enableColumnFilter: false,
            }),
        ];
    },
    'show-active-accounts-table': buildShowAccountsColumns,
    'show-archived-accounts-table': buildShowAccountsColumns,
    'show-gateways-table': buildShowGatewaysColumns,
    batchTransactionTable: ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return buildNullableUnsafeColumns(ch, html, unsafeHTML, [
            ['0', 'ID'],
            ['1', 'Date'],
            ['2', 'BSB'],
            ['3', 'Account Number'],
            ['4', 'Amount'],
            ['5', 'Customer Ref'],
            ['6', 'BPay Ref'],
            ['7', 'Links To'],
            ['8', 'File'],
        ]);
    },
    'account-set-list': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        const htmlCell = (info: CellInfo) => {
            const val = info.getValue();
            return val ? html`${unsafeHTML!(val as string)}` : '';
        };

        return [
            ch.accessor('title', {header: () => 'Title', cell: htmlCell, enableSorting: false}),
            ch.accessor('description', {header: () => 'Description', cell: htmlCell, enableSorting: false}),
            ch.accessor('status', {header: () => 'Status', cell: htmlCell, enableSorting: false}),
            ch.accessor('accounts', {header: () => 'Accounts', enableSorting: false}),
            ch.accessor('action', {header: () => 'Action', cell: htmlCell, enableSorting: false}),
        ];
    },
    'chart-of-accounts-table': ({columnHelper: ch, htmlCell}) => [
        htmlColumn(ch, htmlCell!, '0', 'Title'),
        htmlColumn(ch, htmlCell!, '1', 'Account Code'),
        htmlColumn(ch, htmlCell!, '2', 'Header Account'),
        htmlColumn(ch, htmlCell!, '3', 'Header Account Code'),
        htmlColumn(ch, htmlCell!, '4', 'Account Type'),
        htmlColumn(ch, htmlCell!, '5', 'Status'),
        htmlColumn(ch, htmlCell!, '6', 'Reserved?'),
        htmlColumn(ch, htmlCell!, '7', 'Account Set'),
        actionColumn(ch, htmlCell!, '8'),
    ],
    'payments-list': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return [
            unsafeHtmlColumn(ch, html, unsafeHTML, '0', 'Entity Type', {size: 60}),
            unsafeHtmlColumn(ch, html, unsafeHTML, '1', 'Name'),
            unsafeHtmlColumn(ch, html, unsafeHTML, '2', 'Email'),
            unsafeHtmlColumn(ch, html, unsafeHTML, '3', 'Organization', {size: 100}),
            unsafeHtmlColumn(ch, html, unsafeHTML, '4', 'Card Number', {size: 100}),
            unsafeHtmlColumn(ch, html, unsafeHTML, '5', 'Last Payment', {size: 100}),
            unsafeHtmlColumn(ch, html, unsafeHTML, '6', 'Total Amount', {size: 80, align: 'right'}),
        ];
    },
    'order-item-list': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        const htmlCell = (info: CellInfo) => nullableUnsafeCell(html, unsafeHTML, info);

        return [
            ch.accessor('0', {header: () => 'Details', cell: htmlCell, enableSorting: false}),
            ch.accessor('1', {header: () => 'Payor', cell: htmlCell, enableSorting: false}),
            ch.accessor('2', {header: () => 'Total Amount', cell: htmlCell, enableSorting: false}),
            ch.accessor('3', {header: () => 'Tax Type', cell: htmlCell, enableSorting: false}),
            ch.accessor('4', {header: () => 'Reference', cell: htmlCell, enableSorting: false}),
            ch.accessor('5', {header: () => 'Date', cell: htmlCell, enableSorting: false}),
            ch.accessor('6', {header: () => 'Invoice', cell: htmlCell, enableSorting: false}),
        ];
    },
    'tags-table': ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return [
            ch.accessor('title', {
                header: () => 'Title',
                cell: (info: CellInfo) => {
                    const val = info.getValue() as {url?: string; value?: string} | null;
                    if (val?.url) {
                        return html`<a href="${val.url}">${val.value ?? ''}</a>`;
                    }
                    return val?.value ?? '';
                },
            }),
            ch.accessor('tag_type', {
                header: () => 'Tag Type',
                cell: (info: CellInfo) => html`${unsafeHTML!(info.getValue() as string)}`,
            }),
            ch.accessor('description', {header: () => 'Description'}),
            ch.accessor('tag_code', {header: () => 'Tag Code'}),
            ch.accessor('action', {
                header: () => '',
                cell: (info: CellInfo) => {
                    const val = info.getValue() as {
                        tag_set_id?: string;
                        title?: string;
                        tag_id?: string;
                    } | null;
                    if (val?.tag_set_id) {
                        const label = `Remove ${val.title ?? ''} from this tag set`;
                        return html`<cbar-icon-button
                            data-tooltip=${label}
                            aria-label=${label}
                            href="javascript:void(0);"
                            variant="standard"
                            class="remove_tag_from_set icon_link datatable-action-link datatable-icon-button"
                            tag_set_id=${val.tag_set_id ?? ''}
                            tag_id=${val.tag_id ?? ''}
                        ><cbar-icon aria-hidden="true">delete</cbar-icon></cbar-icon-button>`;
                    }
                    return '';
                },
                enableSorting: false,
                enableColumnFilter: false,
            }),
        ];
    },
    'applied-entities-table': ({columnHelper: ch, html}) => [
        ch.accessor('entity_type', {header: () => 'Type'}),
        ch.accessor('name', {
            header: () => 'Name',
            cell: (info) => {
                const val = info.getValue() as {url?: string; value?: string} | null;
                if (val?.url) {
                    return html`<a href="${val.url}">${val.value ?? ''}</a>`;
                }
                return val?.value ?? '';
            },
        }),
        ch.accessor('action', {
            header: () => 'Action',
            cell: (info) => {
                const row = info.row.original as {
                    tag_set_id?: string;
                    display_name?: string;
                    entity_id?: string;
                    entity_type?: string;
                };
                if (row?.tag_set_id) {
                    const label = `Remove ${row.display_name ?? ''} from this tag set`;
                    return html`<cbar-icon-button
                        data-tooltip=${label}
                        aria-label=${label}
                        href="javascript:void(0);"
                        variant="standard"
                        class="remove_entity_from_tagset datatable-action-link datatable-icon-button"
                        tag_set_id=${row.tag_set_id ?? ''}
                        entity_id=${row.entity_id ?? ''}
                        entity_type=${row.entity_type ?? ''}
                    ><cbar-icon aria-hidden="true">delete</cbar-icon></cbar-icon-button>`;
                }
                return '';
            },
            enableSorting: false,
        }),
    ],
    bank_transactions_table: ({columnHelper: ch, html}) => {
        const unsafeHTML = unsafeHTMLFn();
        return [
            ch.accessor('0', {
                header: () => html`<cbar-checkbox id='selectAll' name='selectAll' value='all'></cbar-checkbox>`,
                cell: (info: CellInfo) => html`${unsafeHTML!(info.getValue() as string)}`,
                size: 40,
                enableSorting: false,
                enableColumnFilter: false,
            }),
            unsafeHtmlColumn(ch, html, unsafeHTML, '1', 'Date', {size: 100}),
            unsafeHtmlColumn(ch, html, unsafeHTML, '2', 'Description', {size: 300}),
            unsafeHtmlColumn(ch, html, unsafeHTML, '3', 'Debit', {size: 75, align: 'right'}),
            unsafeHtmlColumn(ch, html, unsafeHTML, '4', 'Credit', {size: 75, align: 'right'}),
            unsafeHtmlColumn(ch, html, unsafeHTML, '5', 'Balance', {size: 75, align: 'right'}),
            {...unsafeHtmlColumn(ch, html, unsafeHTML, '6', '', {
                enableColumnFilter: false,
                cell: (info: CellInfo) => {
                    const value = info.getValue();
                    if (value == null || value === '') {
                        return '';
                    }
                    const actionsCell = window.CBAR?.datatableActionsCell;
                    return actionsCell
                        ? actionsCell(value as string)
                        : html`${unsafeHTML!(value as string)}`;
                },
            }), size: 220, minSize: 180, align: 'right'},
            unsafeHtmlColumn(ch, html, unsafeHTML, '7', 'Ignored', {size: 10}),
        ];
    },
    'payor-profiles-table': ({columnHelper: ch, htmlCell}, table) => {
        table.availableSizes = ['10', '25', '50'];
        return [
            htmlColumn(ch, htmlCell!, 'payor_html', 'Payor'),
            textColumn(ch, 'address', 'Address'),
            textColumn(ch, 'phone', 'Phone'),
            textColumn(ch, 'email', 'Email'),
        ];
    },
    'payor-payment-methods-table': ({columnHelper: ch, htmlCell}, table) => {
        table.availableSizes = ['10', '25', '50'];
        return [
            htmlColumn(ch, htmlCell!, 'flags_html', '', {enableColumnFilter: false}),
            textColumn(ch, 'cardholder_name', 'Cardholder'),
            htmlColumn(ch, htmlCell!, 'card_type_html', 'Type'),
            htmlColumn(ch, htmlCell!, 'card_number', 'Number'),
            textColumn(ch, 'expiry', 'Expiry'),
            ch.accessor('date_sort', {
                header: () => 'Added',
                cell: (info) => info.row.original.added_date,
                sortDescFirst: true,
            }),
            htmlColumn(ch, htmlCell!, 'saved_for_html', 'Saved for'),
            htmlColumn(ch, htmlCell!, 'status_html', 'Status'),
            htmlColumn(ch, htmlCell!, 'recurring_html', 'Recurring', {enableColumnFilter: false}),
            {
                ...htmlColumn(ch, htmlCell!, 'actions_html', 'Actions', {enableColumnFilter: false}),
                size: 140,
                minSize: 120,
            },
        ];
    },
    'payor-recurring-table': ({columnHelper: ch, htmlCell}, table) => {
        table.availableSizes = ['10', '25', '50'];
        return [
            htmlColumn(ch, htmlCell!, 'payment_instrument', 'Payment instrument'),
            textColumn(ch, 'organisation', 'Organization'),
            textColumn(ch, 'details', 'Details'),
            htmlColumn(ch, htmlCell!, 'amount', 'Amount', {align: 'right'}),
            textColumn(ch, 'status', 'Status'),
            sortableDateColumn(ch, 'starts_on_sort', 'Starts on', 'starts_on'),
            sortableDateColumn(ch, 'expires_on_sort', 'Expires on', 'expires_on'),
            {
                ...actionColumn(ch, htmlCell!, 'actions_html'),
                header: 'Actions',
                size: 140,
                minSize: 120,
            },
        ];
    },
    'payor-invoices-table': ({columnHelper: ch, html, htmlCell}, table) => {
        const unsafeHTML = unsafeHTMLFn();
        table.availableSizes = ['10', '25', '50'];
        return [
            ch.accessor('date_sort', {
                header: () => 'Date',
                cell: (info) => info.row.original.date,
                sortDescFirst: true,
                enableColumnFilter: false,
            }),
            unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML, 'invoice_html', 'Invoice No'),
            textColumn(ch, 'type', 'Type', {enableColumnFilter: true}),
            ch.accessor('status', {
                header: () => 'Status',
                cell: (info) => {
                    const cancelled = info.row.original.is_cancelled;
                    const value = info.getValue();
                    if (cancelled) {
                        return html`<span class="text-danger">${value as string}</span>`;
                    }
                    return value;
                },
                enableColumnFilter: true,
                filterFn: (row, columnId, filterValue) => {
                    if (filterValue == null || filterValue === '') {
                        return true;
                    }
                    return String(row.getValue(columnId) ?? '')
                        .toLowerCase()
                        .includes(String(filterValue).toLowerCase());
                },
            }),
            unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML, 'amount_html', 'Amount', {align: 'right'}),
            {
                ...actionColumn(ch, htmlCell!, 'actions_html'),
                header: 'Action',
            },
        ];
    },
    'payor-bank-accounts-table': ({columnHelper: ch, htmlCell}, table) => {
        table.availableSizes = ['10', '25', '50'];
        return [
            textColumn(ch, 'institution', 'Institution'),
            textColumn(ch, 'account_name', 'Account name'),
            textColumn(ch, 'bsb', 'BSB'),
            textColumn(ch, 'account_number', 'Account number'),
            textColumn(ch, 'customer_reference', 'Customer reference'),
            sortableDateColumn(ch, 'date_added_sort', 'Date', 'date_added', {sortDescFirst: true}),
            htmlColumn(ch, htmlCell!, 'account_html', 'Account'),
        ];
    },
    'payor-payments-table': ({columnHelper: ch, html, htmlCell}, table) => {
        const unsafeHTML = unsafeHTMLFn();
        table.availableSizes = ['10', '25', '50'];
        return [
            ch.accessor('date_sort', {
                header: () => 'Date',
                cell: (info) => info.row.original.date,
                sortDescFirst: true,
                enableColumnFilter: false,
            }),
            unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML, 'payment_instrument', 'Payment Instrument'),
            unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML, 'amount', 'Amount', {align: 'right'}),
            unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML, 'campaign_html', 'Campaign'),
            textColumn(ch, 'status', 'Status', {enableColumnFilter: true}),
            unsafeHtmlColumnWithTextFilter(ch, html, unsafeHTML, 'invoices_html', 'Invoices'),
            htmlColumn(ch, htmlCell!, 'actions_html', 'Actions', {enableColumnFilter: false}),
        ];
    },
};

function buildShowAccountsColumns({columnHelper: ch, html}: CbarDatatableContext & {htmlCell: HtmlCellFn}) {
    return [
        ch.accessor('account_name', {
            header: () => 'Account Name',
            cell: (info) =>
                html`<a href="${info.row.original.account_url}">${info.getValue()}</a>`,
        }),
        ch.accessor('total_amount', {
            header: () => 'Total',
            cell: (info) => {
                const vals = (info.getValue() as Array<{amount: string; currency?: string}>) || [];
                return html`<div style="text-align: right;">
                    ${vals.map((v) =>
                        v.currency
                            ? html`<div class='pull-right'>${v.amount} <span class="label label-currency">${v.currency}</span></div>`
                            : html`<div class='pull-right'>${v.amount}</div>`,
                    )}
                </div>`;
            },
        }),
        ch.accessor('actions', {
            header: () => '',
            cell: (info) => {
                const val = info.getValue() as {
                    view?: string;
                    edit?: string;
                    update_resources?: string;
                } | null;
                if (val) {
                    return datatableViewEditResourcesActions(html, {
                        view: val.view!,
                        edit: val.edit!,
                        update_resources: val.update_resources!,
                    }, {
                        view: 'View Account',
                        edit: 'Edit Account',
                        resources: 'Update Resources',
                    });
                }
                return '';
            },
            enableColumnFilter: false,
        }),
    ];
}

function buildShowGatewaysColumns({columnHelper: ch, html}: CbarDatatableContext & {htmlCell: HtmlCellFn}) {
    return [
        ch.accessor('name', {
            header: () => 'Name',
            cell: (info) => info.getValue() as string,
        }),
        ch.accessor('type', {
            header: () => 'Type',
            cell: (info) => info.getValue() as string,
        }),
        ch.accessor('status', {
            header: () => 'Status',
            cell: (info) => info.getValue() as string,
        }),
        ch.accessor('linked_accounts', {
            header: () => 'Linked accounts',
            cell: (info) => String(info.getValue() ?? 0),
        }),
        ch.accessor('actions', {
            header: () => '',
            cell: (info) => {
                const val = info.getValue() as {edit?: string} | null;
                if (val?.edit) {
                    return datatableIconActionsRow(html, [
                        {href: val.edit, label: 'Edit Gateway', icon: 'edit'},
                    ]);
                }
                return '';
            },
            enableColumnFilter: false,
        }),
    ];
}

async function initProfileDatatables(): Promise<void> {
    const tables = document.querySelectorAll<DatatableElement>('cbar-datatable[data-datatable-profile]');

    await Promise.all(
        Array.from(tables).map(async (table) => {
            if (table.dataset.serverSide === 'true' || !table.id) {
                return;
            }

            const profile = table.dataset.datatableProfile;
            const builder = profile ? PROFILE_BUILDERS[profile] : undefined;
            if (!builder) {
                return;
            }

            await setupDatatable(table.id, builder);
        }),
    );
}

export const PAYOR_PROFILE_DATATABLE_IDS = [
    'payor-payments-table',
    'payor-invoices-table',
    'payor-recurring-table',
    'payor-bank-accounts-table',
    'payor-payment-methods-table',
    'payor-profiles-table',
] as const;

export const PAYOR_TAB_DATATABLE_ID: Record<string, string> = {
    payments: 'payor-payments-table',
    invoices: 'payor-invoices-table',
    recurring_payments: 'payor-recurring-table',
    bank_accounts: 'payor-bank-accounts-table',
    payment_methods: 'payor-payment-methods-table',
    profiles: 'payor-profiles-table',
};

export async function setupDatatablesById(tableIds: readonly string[]): Promise<void> {
    const configs = tableIds.flatMap((tableId) => (
        TABLE_BUILDERS[tableId]
            ? [[tableId, TABLE_BUILDERS[tableId]] as [string, ColumnBuilder]]
            : []
    ));
    await setupDatatables(configs);
}

function pendingDatatableInits(): Array<[string, ColumnBuilder]> {
    const pendingById = Object.entries(TABLE_BUILDERS).flatMap(([tableId, builder]) => {
        const table = document.getElementById(tableId) as DatatableElement | null;
        return table
            && table.dataset.serverSide !== 'true'
            && datatableNeedsColumnInit(table)
            ? [[tableId, builder] as [string, ColumnBuilder]]
            : [];
    });

    const pendingProfiles = Array.from(
        document.querySelectorAll<DatatableElement>('cbar-datatable[data-datatable-profile]'),
    ).flatMap((table) => {
        if (!table.id || table.dataset.serverSide === 'true' || !datatableNeedsColumnInit(table)) {
            return [];
        }

        const profile = table.dataset.datatableProfile;
        const builder = profile ? PROFILE_BUILDERS[profile] : undefined;
        return builder ? [[table.id, builder] as [string, ColumnBuilder]] : [];
    });

    return [...pendingById, ...pendingProfiles];
}

export async function retryPendingDatatableInits(): Promise<void> {
    const maxAttempts = 20;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const pending = pendingDatatableInits();

        if (pending.length === 0) {
            return;
        }

        await Promise.all(pending.map(([tableId, builder]) => setupDatatable(tableId, builder)));

        if (attempt < maxAttempts - 1) {
            await new Promise((resolve) => window.setTimeout(resolve, 50));
        }
    }
}

export async function initPageDatatables(): Promise<void> {
    const fixedConfigs = Object.entries(TABLE_BUILDERS) as Array<[string, ColumnBuilder]>;
    await setupDatatables(fixedConfigs);
    await initProfileDatatables();
    await retryPendingDatatableInits();

    document.querySelectorAll('cbar-datatable[enable-filtering]').forEach((table) => {
        if (table.dataset.serverSide === 'true') {
            return;
        }
        syncDatatableColumnFiltering(table as DatatableElement);
    });

    document.querySelectorAll('cbar-datatable').forEach((table) => {
        if (table instanceof HTMLElement) {
            prepareDatatableScrollChrome(table);
        }
    });
}

void documentReady().then(() => initPageDatatables());

function primeDatatableColumnFiltering(): void {
  document.querySelectorAll('cbar-datatable[enable-filtering]').forEach((table) => {
    (table as DatatableElement).enableFiltering = false;
  });
}

primeDatatableColumnFiltering();

declare global {
    interface Window {
        reloadPayorWidget?: () => void;
    }
}
